import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import Dice3DOverlay from '../components/dice/Dice3DOverlay';
import { useRemoteDiceRoll } from '../components/dice/useRemoteDiceRoll';
import {
  Swords, Heart, Shield, User, Skull, AlertTriangle,
  EyeOff, Moon, Snowflake, Bug, Activity, Sparkles,
  Crown, ChevronRight, Zap, Volume2, VolumeX, RotateCw
} from 'lucide-react';

const API_URL = '/api';

const CONDITION_ICONS = {
  blinded: EyeOff,
  frightened: AlertTriangle,
  paralyzed: Snowflake,
  poisoned: Bug,
  stunned: Sparkles,
  unconscious: Moon,
  prone: ChevronRight,
  exhaustion: Activity,
  concentration: Zap
};

const CONDITION_LABELS = {
  blinded: { fr: 'Aveugle', en: 'Blinded' },
  charmed: { fr: 'Charme', en: 'Charmed' },
  deafened: { fr: 'Assourdi', en: 'Deafened' },
  frightened: { fr: 'Effraye', en: 'Frightened' },
  grappled: { fr: 'Agrippe', en: 'Grappled' },
  incapacitated: { fr: 'Incapable', en: 'Incapacitated' },
  invisible: { fr: 'Invisible', en: 'Invisible' },
  paralyzed: { fr: 'Paralyse', en: 'Paralyzed' },
  petrified: { fr: 'Petrifie', en: 'Petrified' },
  poisoned: { fr: 'Empoisonne', en: 'Poisoned' },
  prone: { fr: 'A terre', en: 'Prone' },
  restrained: { fr: 'Entrave', en: 'Restrained' },
  stunned: { fr: 'Etourdi', en: 'Stunned' },
  unconscious: { fr: 'Inconscient', en: 'Unconscious' },
  exhaustion: { fr: 'Epuisement', en: 'Exhaustion' },
  concentration: { fr: 'Concentration', en: 'Concentration' }
};

const HP_STATUS_COLORS = {
  healthy: { bg: 'bg-green-500', text: 'text-green-500', label: { fr: 'En forme', en: 'Healthy' } },
  injured: { bg: 'bg-yellow-500', text: 'text-yellow-500', label: { fr: 'Blesse', en: 'Injured' } },
  bloodied: { bg: 'bg-orange-500', text: 'text-orange-500', label: { fr: 'Ensanglante', en: 'Bloodied' } },
  critical: { bg: 'bg-red-500', text: 'text-red-500', label: { fr: 'Critique', en: 'Critical' } },
  down: { bg: 'bg-gray-500', text: 'text-gray-500', label: { fr: 'A terre', en: 'Down' } }
};

export default function PlayerCombatViewPage() {
  const { token } = useParams();
  const { activeRoll, observeRoll, clearActiveRoll } = useRemoteDiceRoll(token);
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language] = useState('fr');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const lastTurnKeyRef = useRef(null);

  const fetchView = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/player-view/combat/${token}/live`);
      const nextData = response.data;
      observeRoll(nextData.latest_public_roll || null);

      const nextTurnKey = `${nextData.encounter_id || 'none'}:${nextData.current_turn_index ?? 0}`;
      if (
        soundEnabled
        && lastTurnKeyRef.current !== null
        && nextTurnKey !== lastTurnKeyRef.current
        && nextData.status !== 'idle'
      ) {
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.frequency.value = 800;
          oscillator.type = 'sine';
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);
        } catch (audioError) {
          console.log('Audio not available');
        }
      }

      lastTurnKeyRef.current = nextTurnKey;
      setViewData(nextData);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(language === 'fr' ? 'Lien de vue joueur invalide' : 'Invalid player view link');
      } else {
        setError(language === 'fr' ? 'Erreur de connexion' : 'Connection error');
      }
    } finally {
      setLoading(false);
    }
  }, [token, language, soundEnabled, observeRoll]);

  useEffect(() => {
    fetchView();
  }, [fetchView]);

  // SSE with polling fallback
  useEffect(() => {
    let interval = null;
    let es = null;
    try {
      es = new EventSource(`${API_URL}/player-view/combat/${token}/sse`);
      es.addEventListener('update', () => fetchView());
      es.onerror = () => {
        // Fallback to polling on SSE failure
        es.close();
        es = null;
        if (!interval) interval = setInterval(fetchView, 3000);
      };
    } catch {
      // SSE not supported, use polling
      interval = setInterval(fetchView, 3000);
    }
    return () => {
      if (es) es.close();
      if (interval) clearInterval(interval);
    };
  }, [fetchView, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Swords className="h-16 w-16 animate-pulse text-primary mx-auto mb-4" />
          <p className="text-xl text-foreground">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Swords className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-xl text-foreground mb-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!viewData || viewData.status === 'idle') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-2xl w-full">
          <CardContent className="py-16 text-center">
            <Swords className="h-20 w-20 mx-auto text-primary/70 mb-6" />
            <h1 className="text-3xl font-bold text-foreground mb-3">
              {language === 'fr' ? 'Aucun combat en cours' : 'No combat in progress'}
            </h1>
            <p className="text-muted-foreground text-lg">
              {language === 'fr'
                ? 'La vue joueur s activera automatiquement au demarrage d une rencontre.'
                : 'Player view will update automatically when a combat starts.'}
            </p>
            {viewData?.message && (
              <p className="text-xs text-muted-foreground mt-4 opacity-80">{viewData.message}</p>
            )}
          </CardContent>
        </Card>
        {activeRoll && (
          <Dice3DOverlay rollData={activeRoll} onComplete={clearActiveRoll} />
        )}
      </div>
    );
  }

  const encounter = viewData;

  const visibleCombatants = encounter.combatants.filter((combatant) => combatant.status !== 'dead');
  const currentCombatant = visibleCombatants[encounter.current_turn_index];
  const isCurrentVisible = visibleCombatants.some((combatant) => combatant.id === currentCombatant?.id);
  const eliminatedCount = encounter.combatants.filter(
    (combatant) => combatant.status === 'dead' && combatant.combatant_type !== 'player'
  ).length;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Swords className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-wide">
            {encounter.encounter_name}
          </h1>
        </div>
        {encounter.location && (
          <p className="text-lg text-muted-foreground">{encounter.location}</p>
        )}
        <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
          <Badge className="text-lg px-4 py-1 bg-primary text-primary-foreground">
            Round {encounter.current_round}
          </Badge>
          <Badge variant="outline" className="text-lg px-4 py-1 text-foreground border-border">
            {visibleCombatants.length} {language === 'fr' ? 'combattants' : 'combatants'}
          </Badge>
          {eliminatedCount > 0 && (
            <Badge variant="outline" className="text-lg px-4 py-1 text-red-500 border-red-400/30">
              <Skull className="h-4 w-4 mr-1" />
              {eliminatedCount} {language === 'fr' ? 'elimines' : 'eliminated'}
            </Badge>
          )}

          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={cn(
              'flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors',
              soundEnabled
                ? 'bg-primary/20 text-primary'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            )}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            {language === 'fr' ? 'Son' : 'Sound'}
          </button>
        </div>
      </div>

      {currentCombatant && isCurrentVisible && (
        <div className="max-w-2xl mx-auto mb-8">
          <Card
            className={cn(
              'border-2 shadow-lg',
              currentCombatant.status === 'unconscious'
                ? 'bg-yellow-500/20 border-yellow-500 shadow-yellow-500/20'
                : 'bg-primary/20 border-primary shadow-primary/20'
            )}
          >
            <CardContent className="p-6 text-center">
              <p
                className={cn(
                  'text-sm mb-1 uppercase tracking-wider',
                  currentCombatant.status === 'unconscious' ? 'text-yellow-500' : 'text-primary'
                )}
              >
                {language === 'fr' ? 'Tour actuel' : 'Current turn'}
              </p>
              <div className="flex items-center justify-center gap-3">
                {currentCombatant.status === 'unconscious' ? (
                  <Moon className="h-8 w-8 text-yellow-500" />
                ) : (
                  <Crown className="h-8 w-8 text-primary" />
                )}
                <h2 className="text-3xl font-bold text-foreground">{currentCombatant.name}</h2>
                {currentCombatant.status === 'unconscious' && (
                  <Badge className="bg-yellow-500 text-black">
                    {language === 'fr' ? 'Inconscient' : 'Unconscious'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="grid gap-3">
          {visibleCombatants.map((combatant) => {
            const isCurrentTurn = currentCombatant?.id === combatant.id;
            const isUnconscious = combatant.status === 'unconscious';
            const isDown = combatant.hp_status === 'down' || combatant.current_hp <= 0;

            let hpDisplay;
            if (encounter.hp_display_mode === 'exact') {
              const hpPercent = Math.max(0, (combatant.current_hp / combatant.max_hp) * 100);
              hpDisplay = (
                <div className="flex items-center gap-2 flex-1">
                  <Heart
                    className={cn(
                      'h-5 w-5',
                      isDown ? 'text-gray-500' : hpPercent > 50 ? 'text-green-500' : hpPercent > 25 ? 'text-yellow-500' : 'text-red-500'
                    )}
                  />
                  <div className="flex-1 relative">
                    <Progress value={hpPercent} className="h-4" />
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white drop-shadow">
                      {combatant.current_hp}/{combatant.max_hp}
                      {combatant.temp_hp > 0 && ` (+${combatant.temp_hp})`}
                    </span>
                  </div>
                </div>
              );
            } else {
              const status = HP_STATUS_COLORS[combatant.hp_status] || HP_STATUS_COLORS.healthy;
              hpDisplay = (
                <Badge className={cn('text-sm px-3', status.bg)}>
                  {status.label[language]}
                </Badge>
              );
            }

            return (
              <Card
                key={combatant.id}
                className={cn(
                  'transition-all duration-300',
                  isCurrentTurn && !isUnconscious && 'ring-2 ring-primary shadow-lg shadow-primary/30 scale-[1.02]',
                  isCurrentTurn && isUnconscious && 'ring-2 ring-yellow-500 shadow-lg shadow-yellow-500/30 scale-[1.02]',
                  isUnconscious && 'opacity-70 border-yellow-500/50'
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={cn(
                        'w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 font-bold',
                        isCurrentTurn && !isUnconscious && 'bg-primary text-primary-foreground',
                        isCurrentTurn && isUnconscious && 'bg-yellow-500 text-black',
                        !isCurrentTurn && combatant.combatant_type === 'player' && 'bg-blue-500/20 border-2 border-blue-500 text-blue-600 dark:text-blue-400',
                        !isCurrentTurn && combatant.combatant_type !== 'player' && 'bg-secondary text-secondary-foreground'
                      )}
                    >
                      <span className="text-2xl">{combatant.initiative}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={cn('text-lg font-semibold text-card-foreground', isUnconscious && 'text-yellow-500')}>
                          {combatant.name}
                        </h3>
                        {combatant.combatant_type === 'player' && (
                          <Badge variant="outline" className="text-xs border-blue-500 text-blue-600 dark:text-blue-400">
                            <User className="h-3 w-3 mr-1" />
                            {language === 'fr' ? 'Joueur' : 'Player'}
                          </Badge>
                        )}
                        {isUnconscious && (
                          <Badge className="bg-yellow-500 text-black text-xs">
                            <Moon className="h-3 w-3 mr-1" />
                            {language === 'fr' ? 'Inconscient' : 'Unconscious'}
                          </Badge>
                        )}
                      </div>

                      {combatant.status_effects?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {combatant.status_effects.map((effect, index) => {
                            const Icon = CONDITION_ICONS[effect.name] || AlertTriangle;
                            const label = CONDITION_LABELS[effect.name]?.[language] || effect.name;
                            return (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-xs text-orange-400 border-orange-400/50"
                              >
                                <Icon className="h-3 w-3 mr-1" />
                                {label}
                                {effect.duration && ` (${effect.duration})`}
                              </Badge>
                            );
                          })}
                        </div>
                      )}

                      {combatant.system_stats?.some(field => !['health', 'initiative'].includes(field.role)) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {combatant.system_stats
                            .filter(field => !['health', 'initiative'].includes(field.role))
                            .map((field, index) => (
                              <Badge key={field.id || index} variant="secondary" className="text-xs">
                                {field.label}: {String(field.value ?? '-')}
                              </Badge>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 w-36">
                      {hpDisplay}
                    </div>

                    {combatant.combatant_type === 'player' && combatant.ac && (
                      <div className="flex items-center gap-1 shrink-0 text-muted-foreground">
                        <Shield className="h-5 w-5" />
                        <span className="font-bold">{combatant.ac}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mt-8 text-muted-foreground text-sm">
        <p>{language === 'fr' ? 'Mise a jour automatique' : 'Auto-refresh'}</p>
        <button
          onClick={() => fetchView()}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
          title={language === 'fr' ? 'Rafraîchir' : 'Refresh'}
        >
          <RotateCw className="h-3.5 w-3.5" />
        </button>
      </div>
      {activeRoll && (
        <Dice3DOverlay rollData={activeRoll} onComplete={clearActiveRoll} />
      )}
    </div>
  );
}
